import { Ref, computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import apiGw from '@/services/apiGw';
import { YapMoney } from '@/types/general';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';

interface PaymentsStats {
  count: number;
  amount: string | YapMoney; // string for GET /cash-accounts/{id}/open-payments-stats and YapMoney for /v2/open-payment-stats
  failed: number;
  forecast30Days: {
    count: number;
    amount: string | YapMoney; // string for GET /cash-accounts/{id}/open-payments-stats and YapMoney for /v2/open-payment-stats
  };
  ebill: {
    open: number;
    unread: number;
    overdue: number;
  };
}

export const useOpenPaymentsStatsStore = defineStore('openPaymentsStats', () => {
  const eventStreamStore = useEventStreamStore();
  const cashAccountsStore = useCashAccountsStore();
  const contextSwitchStore = useContextSwitchStore();
  const { selectedCashAccountUid } = storeToRefs(cashAccountsStore);

  // STATES
  const pendingPromise: Ref<{ [key: string]: any }> = ref({});
  const openPaymentStats = ref<Map<string, PaymentsStats>>(new Map());
  // GETTERS
  const openPaymentsStatsForSelectedCashAccount = computed(() => {
    if (!selectedCashAccountUid.value) {
      return undefined;
    }
    return openPaymentStats.value.get(selectedCashAccountUid.value);
  });
  const openPaymentsCount = computed(() => {
    if (
      !openPaymentStats.value ||
      !selectedCashAccountUid.value ||
      !openPaymentStats.value.has(selectedCashAccountUid.value)
    ) {
      return undefined;
    }

    const selectedStats = openPaymentStats.value.get(selectedCashAccountUid.value);
    if (!selectedStats) {
      return undefined;
    }
    return selectedStats.count + (selectedStats.ebill?.open ?? 0);
  });

  // ACTIONS
  function $reset() {
    pendingPromise.value = {};
    openPaymentStats.value = new Map();
  }

  async function fetchOpenPaymentsStats() {
    const cashAccountUid = selectedCashAccountUid.value;
    if (!cashAccountUid) {
      return;
    }
    if (openPaymentStats.value.has(cashAccountUid)) {
      return;
    }

    if (!!pendingPromise.value[cashAccountUid]) {
      await pendingPromise.value[cashAccountUid];
      return;
    }

    const uri = contextSwitchStore.isPrivateContext
      ? `/v2/open-payment-stats`
      : `/cash-accounts/${cashAccountUid}/open-payments-stats`;

    pendingPromise.value[cashAccountUid] = apiGw
      .get(uri)
      .then((data) => {
        openPaymentStats.value.set(cashAccountUid, data.data);
      })
      .finally(() => {
        delete pendingPromise.value[cashAccountUid];
      });

    await pendingPromise.value[cashAccountUid];
  }

  watch(selectedCashAccountUid, fetchOpenPaymentsStats, { immediate: true });

  watch(
    () => eventStreamStore.stale_beneficiaries,
    (newVal) => {
      if (newVal) {
        const contextUids = Object.values(newVal).map((item) => item.context);
        const currentCtx = contextSwitchStore.currentContextUid;
        const cashAccountUid = selectedCashAccountUid.value;
        if (currentCtx && contextUids.includes(currentCtx) && cashAccountUid) {
          // In order to make sure we fetch fresh data, we delete the cache.
          openPaymentStats.value.delete(cashAccountUid);
          fetchOpenPaymentsStats();
        }
      }
    }
  );

  watch(
    () => eventStreamStore.stale_payment_orders,
    (newVal) => {
      if (newVal) {
        const values = Object.values(newVal);
        const staleContextUids = values.map((item) => item.context);
        const staleCashAccountUids = values.map((item) => item.extra?.cashAccountUid);
        const currentCtx = contextSwitchStore.currentContextUid;

        if (
          selectedCashAccountUid.value &&
          currentCtx &&
          staleContextUids.includes(currentCtx) &&
          staleCashAccountUids.includes(selectedCashAccountUid.value)
        ) {
          // In order to make sure we fetch fresh data, we delete the cache.
          openPaymentStats.value.delete(selectedCashAccountUid.value);
          fetchOpenPaymentsStats();
        }
      }
    },
    { deep: true }
  );

  return {
    // STATES
    openPaymentStats,
    // GETTERS
    openPaymentsStatsForSelectedCashAccount,
    openPaymentsCount,

    // ACTIONS
    $reset
  };
});
